import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    Alert
} from "reactstrap";
import { toast } from "react-toastify";
import { API_URL } from "constants/ApiUrl";
import { useTranslation } from "react-i18next";
import { getProfile } from "store/slices/agentSlice";
import countriesLib from "i18n-iso-countries";
import localeFr from "i18n-iso-countries/langs/fr.json";
import localeEn from "i18n-iso-countries/langs/en.json";

const EditAgentProfile = ({ onCancel }) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { agent, loading, error } = useSelector((state) => state.Agent);

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        country: "",
        userName: "",
        licenseNumber: "",
        licenseDocument: null,
        licenseDocumentUrl: "", // pour afficher le fichier existant
    });

    const fileInputRef = useRef(null); // Référence vers le vrai input invisible

    countriesLib.registerLocale(localeFr);
    countriesLib.registerLocale(localeEn);
    const countryObj = countriesLib.getNames(i18n.language || "fr", { select: "official" });
    const countries = Object.values(countryObj);

    useEffect(() => {
        if (agent) {
            setForm({
                firstName: agent.firstName || "",
                lastName: agent.lastName || "",
                email: agent.email || "",
                phone: agent.phone || "",
                country: agent.country || "",
                userName: agent.userName || "",
                licenseNumber: agent.licenseNumber || "",
                licenseDocument: null,
                licenseDocumentUrl: agent.licenseDocument
                    ? `${API_URL}/${agent.licenseDocument}`
                    : "",
            });
        }
    }, [agent]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCustomFileClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileSelected = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({
                ...prev,
                licenseDocument: file,
                licenseDocumentUrl: URL.createObjectURL(file), // affichage local preview
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !form.firstName.trim() ||
            !form.lastName.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.country.trim() ||
            !form.userName.trim() ||
            !form.licenseNumber.trim()
        ) {
            toast.error(t("editProfileAgent.agentFillAllFields"));
            return;
        }

        const formData = new FormData();
        formData.append("firstName", form.firstName);
        formData.append("lastName", form.lastName);
        formData.append("email", form.email);
        formData.append("phone", form.phone);
        formData.append("country", form.country);
        formData.append("userName", form.userName);
        formData.append("licenseNumber", form.licenseNumber);
        if (form.licenseDocument) {
            formData.append("licenseDocument", form.licenseDocument);
        }

        try {
            const res = await fetch(`${API_URL}/api/agent/updateProfile`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("authUser")}`,
                },
                body: formData,
            });

            if (res.ok) {
                dispatch(getProfile());
                onCancel();
                toast.success(t("editProfileAgent.agentUpdatedSuccess"));
            } else {
                const errorData = await res.json();
                toast.error(`${t("editProfileAgent.error")}: ${errorData.message || res.statusText}`);
            }
        } catch (error) {
            toast.error(`${t("editProfileAgent.errorOnUpdate")}: ${error.message}`);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            {error && <Alert color="danger">{error}</Alert>}

            <FormGroup>
                <Label for="firstName">{t("editProfileAgent.agentFirstName")}</Label>
                <Input
                    id="firstName"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    required
                />
            </FormGroup>

            <FormGroup>
                <Label for="lastName">{t("editProfileAgent.agentLastName")}</Label>
                <Input
                    id="lastName"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    required
                />
            </FormGroup>

            <FormGroup>
                <Label for="email">{t("editProfileAgent.agentEmail")}</Label>
                <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                />
            </FormGroup>

            <FormGroup>
                <Label for="phone">{t("editProfileAgent.agentPhone")}</Label>
                <Input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                />
            </FormGroup>

            <FormGroup>
                <Label for="country">{t("editProfileAgent.agentCountry")}</Label>
                <Input
                    type="select"
                    id="country"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    required
                >
                    <option value="">{t("editProfileAgent.selectCountry")}</option>
                    {countries.map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </Input>
            </FormGroup>

            <FormGroup>
                <Label for="userName">{t("editProfileAgent.agentUserName")}</Label>
                <Input
                    id="userName"
                    name="userName"
                    value={form.userName}
                    onChange={handleChange}
                    required
                />
            </FormGroup>

            <FormGroup>
                <Label for="licenseNumber">{t("editProfileAgent.agentLicenseNumber")}</Label>
                <Input
                    id="licenseNumber"
                    name="licenseNumber"
                    value={form.licenseNumber}
                    onChange={handleChange}
                    required
                />
            </FormGroup>

            <FormGroup>
                <Label>{t("editProfileAgent.agentLicenseDocument")}</Label><br />
                <Button color="primary" type="button" onClick={handleCustomFileClick}>
                    {t("editProfileAgent.chooseDocument")}
                </Button>

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/*,application/pdf"
                    onChange={handleFileSelected}
                />

                {form.licenseDocumentUrl && (
                    <div className="mt-2">
                        <a href={form.licenseDocumentUrl} target="_blank" rel="noopener noreferrer">
                            {t("editProfileAgent.viewCurrentDocument")}
                        </a>
                    </div>
                )}
            </FormGroup>

            <Button type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : t("editProfileAgent.save")}
            </Button>{" "}
            <Button type="button" color="secondary" onClick={onCancel} disabled={loading}>
                {t("editProfileAgent.cancel")}
            </Button>
        </Form>
    );
};

export default EditAgentProfile;
